import { useState, useCallback, useRef, useEffect } from "react";
import { PatientBasedAppointment } from "../PatientBasedAppointment";
import { useLocationContext } from "../../../../contexts/LocationContext";

interface UsePatientStatsProps {
  /** ID do profissional para filtrar (opcional) */
  practitionerActor?: string;
  /** Indica se deve retornar apenas o resumo (opcional) */
  summary?: boolean;
}

interface UsePatientStatsResult {
  /** Dados dos pacientes */
  data: fhir5.Bundle<fhir5.Patient> | undefined;
  /** Estado de carregamento */
  loading: boolean;
  /** Estado de erro */
  error: Error | null;
  /** Função para buscar os dados */
  fetchStats: () => Promise<void>;
}

/**
 * Hook para buscar estatísticas de pacientes baseado em agendamentos
 *
 * @example
 * ```tsx
 * const { data, loading, error, fetchStats } = usePatientStats({
 *   practitionerActor: 'Practitioner/1',
 *   summary: true
 * });
 *
 * // Buscar dados
 * useEffect(() => {
 *   fetchStats();
 * }, [fetchStats]);
 * ```
 */
export function usePatientStats(
  props: UsePatientStatsProps = {}
): UsePatientStatsResult {
  const [data, setData] = useState<fhir5.Bundle<fhir5.Patient>>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { location } = useLocationContext();

  // Mantém uma referência estável dos parâmetros
  const paramsRef = useRef(props);

  // Atualiza a referência quando as props mudam
  useEffect(() => {
    paramsRef.current = props;
  }, [props]);

  const fetchStats = useCallback(async () => {
    const currentParams = paramsRef.current;

    // Prepara array de locations
    const locationActors = location
      ?.map((loc) => `Location/${loc.resource?.id}`)
      .filter(Boolean)
      .join(",");

    // Evita chamada se não houver locations
    if (!locationActors) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await PatientBasedAppointment({
        locationActors,
        practitionerActor: currentParams.practitionerActor,
        summary: currentParams.summary,
      });

      setData(response);
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error("Erro ao buscar estatísticas de pacientes")
      );
      console.error("usePatientStats error:", err);
    } finally {
      setLoading(false);
    }
  }, [location]); // fetchStats depende apenas do location context

  return {
    data,
    loading,
    error,
    fetchStats,
  };
}
